import { Box, Center, Flex, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch, useLocation } from "react-router-dom";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import { Spinner } from "../../components/Loader";
import Sidebar from "../../components/Sidebar";
import { dropAnimation } from "../../components/styles/style";
import { useAuth } from "../../helpers/configs/plugins/CookieAuthPlugin";

// * lazy load component
const DelayImport = (path) => {
  return React.lazy(() => {
    return Promise.all([path, new Promise((resolve) => setTimeout(resolve, 300))]).then(([moduleExports]) => moduleExports);
  });
};

const Promotion = DelayImport(import("./dashboards/promotion"));
const NewsFeed = DelayImport(import("./dashboards/news-feed"));
const Partnership = DelayImport(import("./dashboards/partnership"));
const Doctor = DelayImport(import("./dashboards/doctor"));

const ReportFeedback = DelayImport(import("./reports/FeedBack"));
const ReportRating = DelayImport(import("./reports/Rating"));
const ReportAppointment = DelayImport(import("./reports/Appointment"));

const UserApp = DelayImport(import("./setting/user-app"));
const AboutUs = DelayImport(import("./setting/about-us"));
const PrivacyTerms = DelayImport(import("./setting/privacy-terms"));
const SystemRole = DelayImport(import("./setting/system-role"));
const User = DelayImport(import("./setting/user"));
const ChangePassword = DelayImport(import("./change-password"));

const routeItem = [
  // * dashboard
  { path: "/promotion", component: <Promotion /> },
  { path: "/news-feed", component: <NewsFeed /> },
  { path: "/partnership", component: <Partnership /> },
  { path: "/doctor", component: <Doctor /> },

  // * report
  { path: "/report-feedback", component: <ReportFeedback /> },
  { path: "/report-rating", component: <ReportRating /> },
  { path: "/report-appointment", component: <ReportAppointment /> },

  // * setting
  { path: "/user-app", component: <UserApp /> },
  { path: "/about-us", component: <AboutUs /> },
  { path: "/privacy-terms", component: <PrivacyTerms /> },
  { path: "/system-role", component: <SystemRole /> },
  { path: "/user", component: <User /> },
  { path: "/change-password", component: <ChangePassword /> },
];

const MotionBox = motion(Box);
export default function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { pathname } = useLocation();
  const [moduleName, setModuleName] = useState({ name: "", action: "" });
  const { username } = useAuth()?.user || {};
  const isEn = i18n.language === "en";

  // * get module name from path
  useEffect(() => {
    const pathParts = pathname.split("/").filter(Boolean);
    if (pathParts.length === 0) {
      setModuleName({ name: "", action: "" });
      return;
    }

    const lastPart = pathParts[pathParts.length - 1];
    if (lastPart === "list") {
      const mainPath = pathParts[pathParts.length - 2];
      const pathSegment = mainPath?.replace(/-/g, "_") || "";
      setModuleName({ name: pathSegment, action: "" });
      return;
    }

    // Check if the last part is an ID (numeric)
    const isLastPartId = /^\d+$/.test(lastPart);
    const actionPart = isLastPartId ? pathParts[pathParts.length - 2] : lastPart;
    const mainPathIndex = isLastPartId ? pathParts.length - 3 : pathParts.length - 2;
    const mainPath = mainPathIndex >= 0 ? pathParts[mainPathIndex] : pathParts[0]; // Fallback to first segment if index is invalid
    const pathSegment = mainPath?.replace(/-/g, "_") || "";
    const validActions = ["add", "view", "edit", "reset", "clone"];
    if (validActions.includes(actionPart)) {
      setModuleName({ name: pathSegment, action: actionPart });
    } else {
      setModuleName({ name: pathSegment, action: "" });
    }
  }, [pathname]);

  return (
    <Flex position="relative" h="100vh" overflow="auto" id="app-container">
      <Box display="flex">
        <Sidebar />
      </Box>
      <Box w="calc(100% - 18rem)" position="relative" flex="1" overflow="auto">
        <Flex justifyContent="space-between" alignItems="center" h="40px" boxShadow="sm">
          <Text fontWeight="semibold" ml="4" fontSize="18px">
            {(moduleName.action && t("label." + moduleName?.action)) + (isEn ? " " : "") + (moduleName.name && t("label." + moduleName?.name))}
          </Text>
        </Flex>
        <Box p="2" h="calc(100vh - 40px)">
          <Suspense
            fallback={
              <Center h="80vh" w="full">
                <Spinner />
              </Center>
            }
          >
            <AnimatePresence exitBeforeEnter>
              <Switch location={location} key={location.pathname}>
                {routeItem.map(({ path, component }) => {
                  // super admin can't change password
                  if (path === "/change-password" && username === "super_admin") {
                    return <Redirect to="/" />;
                  }
                  return (
                    <Route key={path} path={path}>
                      <MotionBox h="full" overflow="auto" rounded="md" key={location.pathname} {...dropAnimation}>
                        {component}
                      </MotionBox>
                    </Route>
                  );
                })}
              </Switch>
            </AnimatePresence>
          </Suspense>
        </Box>
      </Box>
    </Flex>
  );
}
